import { useTranslation } from "@redotech/redo-i18n";
import type { ReturnsCenter } from "@redotech/redo-merchant-managed-returns-rpc/schema/get-returns-centers";
import { RedoDrillDownMultiSelect } from "@redotech/redo-web/drill-down-multi-select";
import { memo, useMemo } from "react";

const regionDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });

function countryDisplayName(code: string): string {
  try {
    return regionDisplayNames.of(code) ?? code;
  } catch {
    return code;
  }
}

export const RegionGroupedReturnsCenterSelector = memo(
  function RegionGroupedReturnsCenterSelector({
    allCenters,
    selectedIds,
    onToggleSingle,
    onBulkToggle,
  }: {
    allCenters: readonly ReturnsCenter[];
    selectedIds: Set<string>;
    onToggleSingle: (centerId: string) => void;
    onBulkToggle: (centerIds: string[], select: boolean) => void;
  }) {
    const { t } = useTranslation();
    const groups = useMemo(() => {
      const codes = Array.from(
        new Set(
          allCenters
            .map((c) => c.country?.toUpperCase() ?? null)
            .filter((c): c is string => c !== null),
        ),
      );
      return codes
        .map((code) => ({ key: code, label: countryDisplayName(code) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [allCenters]);

    const items = useMemo(
      () =>
        allCenters
          .filter(
            (center): center is typeof center & { country: string } =>
              center.country !== null,
          )
          .map((center) => ({
            id: center.id,
            groupKey: center.country.toUpperCase(),
            label: center.name,
            sublabel:
              [center.city, center.state].filter(Boolean).join(", ") ||
              undefined,
          })),
      [allCenters],
    );

    return (
      <RedoDrillDownMultiSelect
        emptyGroupMessage={t("No centers in this region yet.")}
        groupLabel="region"
        groups={groups}
        itemLabel="center"
        items={items}
        onBulkToggle={onBulkToggle}
        onToggleSingle={onToggleSingle}
        selectedIds={selectedIds}
      />
    );
  },
);
